import type { AreaCatalog } from '../area';
import type { resolution as en } from '../en/resolution';

export const resolution = {
  // --- Statements ------------------------------------------------------------
  'resolution.statement.delivered': 'Zur Review bereit gemeldet.',
  'resolution.statement.closed': 'Fertiggestellt.',
  'resolution.statement.done': 'Fertig.',
  'resolution.statement.readyForReview': 'Bereit zur Review.',
  'resolution.statement.acceptedAndDone': 'Angenommen und fertig.',
  'resolution.statement.reviewRequired': 'Review erforderlich.',
  'resolution.statement.reviewNotRequired': 'Review nicht erforderlich.',
  'resolution.statement.deliveredAndClosed': 'Fertig.',
  'resolution.statement.seated': 'Ist jetzt zuständig.',
  'resolution.statement.seatedClosed': 'Ist jetzt zuständig. Fertig.',
  'resolution.statement.seatedDeliveredAndClosed': 'Ist jetzt zuständig. Fertig.',
  'resolution.statement.deliveredWaitsOnReview': 'Bereit zur Review.',
  'resolution.statement.seatedWaitsOnReview': 'Ist jetzt zuständig. Wartet auf die Review.',
  'resolution.statement.seatedDeliveredWaitsOnReview': 'Ist jetzt zuständig. Bereit zur Review.',
  'resolution.statement.accepted': 'Abgenommen.',
  'resolution.statement.denied': 'Abgelehnt.',
  'resolution.statement.verdictWithdrawn': 'Bewertung zurückgezogen.',
  'resolution.statement.deliveryTakenBack': 'Als nicht fertig markiert.',
  'resolution.statement.reopened': 'Wieder geöffnet.',
  'resolution.statement.cancelled': 'Abgebrochen.',
  'resolution.statement.takenBack': 'Zurückgenommen.',
  'resolution.statement.markedActive': 'Als aktiv markiert.',
  'resolution.statement.markedIdle': 'Als ruhend markiert.',
  'resolution.statement.commented': 'Kommentiert.',
  'resolution.statement.acknowledged': 'Zur Kenntnis genommen.',
  'resolution.statement.ownerSet': 'Verantwortliche festgelegt.',
  'resolution.statement.doerSet': 'Zuständige festgelegt.',
  'resolution.statement.changed': 'Geändert.',
  'resolution.statement.tagged': '{tags} hinzugefügt.',
  'resolution.statement.untagged': '#{tag} entfernt.',
  'resolution.statement.renamed': 'Umbenannt.',
  'resolution.statement.renamedAndTagged': 'Umbenannt; {tags} hinzugefügt.',
  'resolution.statement.retagged': '#{from} in #{to} geändert.',
  'resolution.statement.tagRenamed': {
    one: '#{from} in #{to} umbenannt, bei {count} Aufgabe.',
    other: '#{from} in #{to} umbenannt, bei {count} Aufgaben.',
  },
  'resolution.statement.moved': 'Nach „{stage}“ verschoben.',

  // --- Consequences ----------------------------------------------------------
  'resolution.consequence.dependenciesOpen': {
    one: '{count} Aufgabe, von der sie abhängt, ist noch offen.',
    other: '{count} Aufgaben, von denen sie abhängt, sind noch offen.',
  },
  'resolution.consequence.subTasksStayOpen': {
    one: '{count} Unteraufgabe bleibt offen.',
    other: '{count} Unteraufgaben bleiben offen.',
  },
  'resolution.consequence.alsoFinishesSubTasks': {
    one: 'Stellt auch {count} Unteraufgabe fertig.',
    other: 'Stellt auch {count} Unteraufgaben fertig.',
  },
  'resolution.consequence.alsoCancelsSubTasks': {
    one: 'Bricht auch {count} Unteraufgabe ab.',
    other: 'Bricht auch {count} Unteraufgaben ab.',
  },

  // --- The conclusion question (§6) -------------------------------------------
  'resolution.question.subTasksOpen.done': {
    one: '{count} Unteraufgabe ist noch offen. Auch fertigstellen?',
    other: '{count} Unteraufgaben sind noch offen. Auch fertigstellen?',
  },
  'resolution.question.subTasksOpen.cancel': {
    one: '{count} Unteraufgabe ist noch offen. Auch abbrechen?',
    other: '{count} Unteraufgaben sind noch offen. Auch abbrechen?',
  },
  'resolution.answer.finishToo': { one: 'Auch fertigstellen', other: 'Auch fertigstellen' },
  'resolution.answer.cancelToo': { one: 'Auch abbrechen', other: 'Auch abbrechen' },
  'resolution.answer.finishAll': 'Alle fertigstellen',
  'resolution.answer.cancelAll': 'Alle abbrechen',
  'resolution.answer.leaveOpen': { one: 'Offen lassen', other: 'Offen lassen' },
  'resolution.stays.tied': {
    one: '{subject} bleibt offen: andere Aufgaben brauchen sie noch.',
    other: '{subject} bleiben offen: andere Aufgaben brauchen sie noch.',
  },
  'resolution.stays.locked': {
    one: '{subject} bleibt offen: nicht von dir abzuschließen.',
    other: '{subject} bleiben offen: nicht von dir abzuschließen.',
  },
  'resolution.stays.held': {
    one: '{subject} bleibt offen: die übergeordnete Aufgabe bleibt offen.',
    other: '{subject} bleiben offen: die übergeordneten Aufgaben bleiben offen.',
  },
  'resolution.subject.subTasks': { one: '{count} Unteraufgabe', other: '{count} Unteraufgaben' },
  'resolution.force.closesNamed': 'Nimmt {titles} mit, obwohl andere Aufgaben sie noch brauchen.',
  'resolution.force.alsoClosesNamed':
    'Nimmt auch {titles} mit, obwohl andere Aufgaben sie noch brauchen.',
  'resolution.force.closesMore': {
    one: 'Nimmt {count} weitere Unteraufgabe mit, obwohl andere Aufgaben sie noch brauchen.',
    other: 'Nimmt {count} weitere Unteraufgaben mit, obwohl andere Aufgaben sie noch brauchen.',
  },
  'resolution.force.alsoClosesMore': {
    one: 'Nimmt auch {count} weitere Unteraufgabe mit, obwohl andere Aufgaben sie noch brauchen.',
    other:
      'Nimmt auch {count} weitere Unteraufgaben mit, obwohl andere Aufgaben sie noch brauchen.',
  },
  'resolution.subject.requesterOnly': 'nur Verantwortliche',

  // --- Permissions (§1 step 5) ------------------------------------------------
  'resolution.lane.viewOnly': 'Du kannst diese Aufgabe sehen, aber nicht bearbeiten.',
  'resolution.lane.ownerReach':
    'Verantwortliche ersetzen dürfen nur Verantwortliche; frag eine von ihnen.',
  'resolution.lane.ruleNeedsAnswer':
    'Die Frage „{questionClass}“ braucht eine Antwort, die diese Regel nicht mitbringt.',
  'resolution.question.outOfRole':
    'Das ist nicht deine Entscheidung, und das Protokoll hält es fest. Trotzdem fortfahren?',
  'resolution.answer.goAhead': 'Fortfahren',
  'resolution.answer.leaveIt': 'Lassen',

  // --- Refusals ----------------------------------------------------------------
  'resolution.refusal.nothingToDo': 'Nichts zu tun.',
  'resolution.refusal.alreadyClosed': 'Schon fertig oder abgebrochen.',
  'resolution.refusal.alreadyEnded':
    'Schon fertig oder abgebrochen. Öffne die Aufgabe zuerst wieder.',
  'resolution.refusal.alreadyOpen': 'Schon offen.',
  'resolution.refusal.alreadyInReview': 'Schon in Review.',
  'resolution.refusal.inReview':
    'Die Aufgabe ist in Review. Annehmen kann die Person, die sie prüft.',
  'resolution.refusal.reviewRequired':
    'Hier ist eine Review erforderlich. Melde die Aufgabe bereit zur Review, oder schalte die Anforderung zuerst ab.',
  'resolution.refusal.nobodyToReview':
    'Niemand sonst ist an dieser Aufgabe beteiligt, um sie zu prüfen.',
  'resolution.refusal.stageHandsIn':
    'Der Flow übergibt die Arbeit. Verschieb sie in die nächste Phase.',
  'resolution.refusal.notInReview': 'Die Aufgabe ist nicht in Review.',
  'resolution.refusal.reviewersAct': 'Annehmen und Ablehnen sind Sache der Person, die prüft.',
  'resolution.refusal.waitsOnReview': 'Die Aufgabe wartet auf die Review.',
  'resolution.refusal.verdictIsRecord':
    'Die Bewertung hält eine Review fest. Nimm die Arbeit an oder lehne sie ab.',
  'resolution.refusal.alreadyThere': 'Schon dort; nichts zu tun.',
  'resolution.refusal.leftAlone': 'Gelassen; es wurde nichts getan.',
  'resolution.refusal.nothingToTakeBack': 'Hier gibt es nichts zurückzunehmen.',
  'resolution.refusal.notYoursToTakeBack':
    'Einen Teil davon darfst du nicht zurücknehmen; es wurde nichts getan.',
  'resolution.refusal.partCannotBeTakenBack':
    'Ein Teil davon lässt sich hier nicht zurücknehmen; es wurde nichts getan.',
  'resolution.refusal.noInverse':
    '„{command}“ lässt sich nicht einzeln zurücknehmen; bearbeite stattdessen die Aufgabe.',
  'resolution.refusal.titleNeeded': 'Eine Aufgabe braucht neben ihren Tags einen Titel.',
  'resolution.refusal.tagUnchanged': 'Der Tag ist unverändert.',
  'resolution.refusal.notTagged': 'Trägt #{tag} nicht.',
  'resolution.refusal.notAMember': 'Diese Aufgabe gehört nicht zu diesem Ablauf.',
  'resolution.refusal.noSuchStage': 'Keine Phase „{stage}“ in diesem Ablauf.',
  'resolution.refusal.gateClosed': 'Die Schwelle zu dieser Phase lässt die Aufgabe nicht durch.',
  'resolution.refusal.tooLarge':
    'Zu groß für einen Schritt ({count} Aufgaben). Stell zuerst die Unteraufgaben fertig.',
  'resolution.refusal.notGathered':
    'Die offenen Unteraufgaben wurden nicht geladen; es wurde nichts getan.',
  'resolution.refusal.notYoursToConclude':
    'Einiges davon darfst du nicht abschließen; es wurde nichts getan.',
  'resolution.refusal.notAnAnswer': '„{answer}“ ist keine Antwort auf diese Frage.',
  'resolution.refusal.nobodyToAnswer':
    'Niemand kann hier die Frage „{questionClass}“ beantworten; es wurde nichts getan.',
  'resolution.refusal.ruleSkips':
    'Diese Regel überspringt die Frage „{questionClass}“; es wurde nichts getan.',
  'resolution.refusal.noPolicy':
    'Für die Frage „{questionClass}“ gibt es hier keine Vorgabe; es wurde nichts getan.',
  'resolution.refusal.movedSinceLooked':
    'Diese Aufgabe hat sich geändert, seit du sie angesehen hast; es wurde nichts getan. Versuch es noch einmal.',
  'resolution.refusal.movedDuringReplay':
    'Diese Aufgabe hat sich geändert, während die gespeicherten Schritte nachgeholt wurden; es wurde nichts getan.',
  'resolution.refusal.queuedTapCannotAnswer':
    '{question} Dafür braucht es eine Antwort, die ein gespeicherter Tipp nicht geben kann.',
} as const satisfies AreaCatalog<typeof en>;
